import { useGetPaymentDetails } from '@/apis/payment_tasks/useGetPaymentDetails'
import { DocumentsViewer } from '@/app/common/components/PaymentElements/DocumentViewer'
import { PurseMovementCard } from '@/app/common/components/PaymentElements/PurseMovementCard'
import PaymentStatusBadge from '@/app/common/components/PaymentStatusBadge'
import { currencyFormatter } from '@/app/common/utilities'
import { Card, CardContent, CardHeader } from '@/common/components/Card.tsx'
import {
  FormRowLabel,
  FormRowLayout,
  FormRowValue,
} from '@/common/components/FormRow/FormRow.tsx'
import Image from '@/common/components/Image'
import LoadingCard from '@/common/components/LoadingCard'
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  FailCircleIcon,
  InfoCircleIcon,
} from '@/common/icons/general'
import { RootState } from '@/storage/store'
import { useSelector } from 'react-redux'
import { removeUnderscoresAndMakeFirstLetterCapital } from '../payment-approval-rules/utils'
import PaymentReviewPopup from './PaymentReviewPopup/PaymentReviewPopup'
import { Badge } from '@/shad/ui/badge'

export default function PaymentDetailsPage() {
  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)
  const paymentId = location.pathname.split('/')[2]

  const {
    data: transaction,
    refetch,
    status,
  } = useGetPaymentDetails({
    programId: selectedProgramYear?.id,
    paymentId,
  })

  if (status === 'pending') {
    return <LoadingCard title="Loading Payment Details" />
  }

  if (status === 'error') {
    return <div>Error</div>
  }

  return (
    <div className="grid gap-4">
      <Card className="space-y-6 h-full">
        <CardContent className="space-y-6">
          <div className="flex justify-between gap-4">
            <div className="flex gap-2">
              {transaction?.status === 'Approved' ? (
                <CheckCircleIcon className="fill-emerald-700 text-xl text-emerald-700" />
              ) : transaction?.status == 'Declined' ? (
                <ExclamationCircleIcon className="fill-red-700 text-xl text-red-700" />
              ) : (
                <InfoCircleIcon className="fill-blue-500 text-xl text-blue-500" />
              )}
              <div>
                <div className="font-medium text-gray-800">
                  Payment{' '}
                  {transaction?.status == 'Approved'
                    ? 'Accepted'
                    : transaction?.status == 'Declined'
                      ? 'Rejected'
                      : ''}
                </div>
                <div className="text-gray-600 text-sm">
                  {transaction?.status == 'In review Admin'
                    ? 'Payments need to be reviewed and approved before they can be processed'
                    : ''}
                </div>
              </div>
            </div>
            {transaction?.status && (
              <div className="text-blue-600 font-medium text-sm">
                <PaymentReviewPopup
                  refetch={refetch}
                  payment_id={transaction?.payment_id}
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex items-center justify-between">
            <h2 className="text-lg font-bold">Transaction Details</h2>

            <PaymentStatusBadge value={transaction.status} />
          </CardHeader>
          <CardContent>
            <FormRowLayout>
              <FormRowLabel>Transaction ID</FormRowLabel>
              <FormRowValue>{transaction?.payment_id ?? ''}</FormRowValue>
            </FormRowLayout>

            <FormRowLayout>
              <FormRowLabel>Transaction Type</FormRowLabel>
              <FormRowValue>{transaction?.transaction_type ?? ''}</FormRowValue>
            </FormRowLayout>

            <FormRowLayout>
              <FormRowLabel>Provider Name</FormRowLabel>
              <FormRowValue>{transaction?.provider_name ?? ''}</FormRowValue>
            </FormRowLayout>

            <FormRowLayout>
              <FormRowLabel>Applicant Name</FormRowLabel>
              <FormRowValue>{transaction?.applicant_name}</FormRowValue>
            </FormRowLayout>

            <FormRowLayout>
              <FormRowLabel>Student Name</FormRowLabel>
              <FormRowValue>
                {transaction?.students
                  .map((student) => student.student_name)
                  .join(', ')}
              </FormRowValue>
            </FormRowLayout>
            {transaction?.tracking_number && (
              <FormRowLayout>
                <FormRowLabel>Tracking Number</FormRowLabel>
                <FormRowValue>
                  {transaction?.tracking_number ?? ''}
                </FormRowValue>
              </FormRowLayout>
            )}
            <FormRowLayout>
              <FormRowLabel>Invoice Number</FormRowLabel>
              <FormRowValue>{transaction?.invoice_quote ?? 'N/A'}</FormRowValue>
            </FormRowLayout>
            <FormRowLayout className="flex-col items-start justify-start">
              <FormRowLabel>Comments</FormRowLabel>
              <FormRowValue>{transaction?.comments ?? 'N/A'}</FormRowValue>
            </FormRowLayout>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader className="flex items-center justify-between">
              <h2 className="text-lg font-bold">Triggered Rules</h2>
            </CardHeader>
            <CardContent className="space-y-6">
              {transaction.approval_rules.map((rule, index) => (
                <div
                  key={`${rule.target_name}-${index}`}
                  className="space-y-2 border-b border-gray-100 pb-4"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-gray-800">{rule.name}</h3>
                    <div className="space-x-2">
                      <Badge variant={'secondary'}>
                        {removeUnderscoresAndMakeFirstLetterCapital(
                          rule.target_type_code
                        )}
                      </Badge>
                      <Badge variant={'secondary'}>{rule.target_name}</Badge>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mt-1">
                    {rule.description}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="text-lg font-bold">Amounts</h2>
            </CardHeader>
            <CardContent>
              <FormRowLayout>
                <FormRowLabel>Payment Amount</FormRowLabel>
                <FormRowValue>
                  {currencyFormatter.format(transaction.total_amount)}
                </FormRowValue>
              </FormRowLayout>

              <FormRowLayout>
                <FormRowLabel>Service Fee</FormRowLabel>
                <FormRowValue>
                  {currencyFormatter.format(transaction.service_fee)}
                </FormRowValue>
              </FormRowLayout>
              <FormRowLayout>
                <FormRowLabel>Amount to Provider</FormRowLabel>
                <FormRowValue>
                  {currencyFormatter.format(transaction.amount_provider)}
                </FormRowValue>
              </FormRowLayout>
            </CardContent>
          </Card>
        </div>

        {transaction.status === 'Appeal awaiting decision' &&
          transaction.applicant_appeal_message && (
            <Card>
              <CardHeader className="text-lg font-bold space-x-2">
                <FailCircleIcon className="text-red-500 mr-2" />
                Applicant Appeal Message
              </CardHeader>
              <CardContent className="break-words overflow-wrap-anywhere whitespace-pre-wrap">
                {transaction.applicant_appeal_message}
              </CardContent>
            </Card>
          )}
        {transaction?.status == 'Appeal awaiting decision' &&
          transaction.applicant_review_comment && (
            <Card>
              <CardHeader className="text-lg font-bold space-x-2">
                <FailCircleIcon className="text-red-500 mr-2" />
                Applicant Rejection Notice
              </CardHeader>
              <CardContent>{transaction.applicant_review_comment}</CardContent>
            </Card>
          )}

        {transaction?.type == 4 &&
          transaction?.status == 'Appeal awaiting decision' &&
          transaction.provider_review_comment && (
            <Card>
              <CardHeader className="text-lg font-bold space-x-2">
                <FailCircleIcon className="text-red-500 mr-2" />
                Provider Appeal Comment
              </CardHeader>
              <CardContent>{transaction.provider_review_comment}</CardContent>
            </Card>
          )}

        {Boolean(transaction.has_appealed) &&
          [1, 5].includes(Number(transaction.status)) && (
            <Card>
              <CardHeader className="text-lg font-bold space-x-2">
                <CheckCircleIcon className="text-primary mr-2" />
                Approval Reason
              </CardHeader>
              <CardContent>{transaction.last_message}</CardContent>
            </Card>
          )}

        {transaction && transaction?.payment_items?.length > 0 && (
          <Card>
            <CardHeader>
              <h2 className="text-lg font-bold">Products</h2>
            </CardHeader>
            <CardContent>
              <div className="[&>*:nth-child(odd)]:bg-gray-50 space-y-4">
                {transaction?.payment_items.map((item) => (
                  <div className="flex items-center gap-2">
                    <div className="h-14 w-14 flex-shrink-0 relative">
                      <div className="absolute h-5 w-5 bg-gray-100 flex items-center justify-center -top-1 -right-1 z-50 border border-gray-300 rounded-full">
                        {item.quantity}
                      </div>
                      {item.provider_item.image && (
                        <Image
                          className="h-14 w-14 flex-shrink-0 rounded-md object-cover"
                          path={item.provider_item.image}
                        />
                      )}
                    </div>
                    <div>
                      <div className="font-normal text-sm">
                        {item.provider_item.name}
                      </div>
                      <div className="font-normal">
                        <span className="font-bold">
                          {currencyFormatter.format(item.price)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        <DocumentsViewer documents={transaction?.payment_documents} />
        <Card>
          <CardHeader>
            <h2 className="font-bold">Categories</h2>
          </CardHeader>
          <CardContent>
            {transaction?.expense_categories.map((category) => {
              return (
                <FormRowLayout key={category.id}>
                  <FormRowLabel>{category.name}</FormRowLabel>
                  <FormRowValue>
                    {currencyFormatter.format(category.amount)}
                  </FormRowValue>
                </FormRowLayout>
              )
            })}
          </CardContent>
        </Card>

        <PurseMovementCard purses={transaction.purse_movements} />
      </div>
    </div>
  )
}
